import { useState } from 'react';
import { CreateRoomModalHeader } from './create/CreateRoomModalHeader';
import { CreateRoomImageField } from './create/CreateRoomImageField';
import { CreateRoomModalActions } from './create/CreateRoomModalActions';
import { useCreateRoomImage } from './create/useCreateRoomImage';
import {
  DEFAULT_ROOM_SCENE_ID,
  ROOM_SCENE_LABELS,
  type RoomSceneId,
} from '../../types/room';

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, sceneId: RoomSceneId, imageFile?: File) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Modal for creating a new persistent room.
 */
export function CreateRoomModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: CreateRoomModalProps) {
  const [name, setName] = useState('');
  const [sceneId, setSceneId] = useState<RoomSceneId>(DEFAULT_ROOM_SCENE_ID);
  const [error, setError] = useState<string | null>(null);
  const {
    imageFile,
    imagePreview,
    fileInputRef,
    handleImageChange,
    handleRemoveImage,
    resetImageState,
  } = useCreateRoomImage(setError);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter a room name');
      return;
    }

    try {
      await onSubmit(name.trim(), sceneId, imageFile || undefined);
      handleClose();
    } catch {
      setError('Failed to create room');
    }
  };

  const handleClose = () => {
    setName('');
    setSceneId(DEFAULT_ROOM_SCENE_ID);
    resetImageState();
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative z-10 w-full max-w-md bg-[#1a1a1a] border border-border rounded-xl shadow-2xl animate-scale-in">
        <CreateRoomModalHeader onClose={handleClose} />

        <form onSubmit={handleSubmit} className="p-5">
          {error && (
            <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
              {error}
            </div>
          )}

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-300 mb-2">Room Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., The Viking Room"
              className="w-full px-3 py-2.5 bg-[#0f0f0f] border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-300 mb-2">Theme</label>
            <select
              value={sceneId}
              onChange={(e) => setSceneId(e.target.value as RoomSceneId)}
              className="w-full px-3 py-2.5 bg-[#0f0f0f] border border-border rounded-lg text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              disabled
            >
              <option value="sci_fi_office">{ROOM_SCENE_LABELS.sci_fi_office}</option>
            </select>
            <p className="mt-2 text-xs text-gray-400">More Rooms Coming Soon</p>
          </div>

          <CreateRoomImageField
            imagePreview={imagePreview}
            isLoading={isLoading}
            onRemoveImage={handleRemoveImage}
            onOpenPicker={() => fileInputRef.current?.click()}
            onChange={handleImageChange}
            inputRef={fileInputRef}
          />

          <CreateRoomModalActions
            isLoading={isLoading}
            isSubmitDisabled={!name.trim()}
            onCancel={handleClose}
          />
        </form>
      </div>
    </div>
  );
}
